import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { StatusPill, Skeleton, Input, Button, TabsRoot, TabsList, TabsTrigger, TabsContent } from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { trpc } from "../../../lib/trpc.ts";
import {
  SAMPLE_PHASE_LABEL,
  SAMPLE_PHASE_TONE,
  sampleMoney,
  shortDate,
} from "./sample-format.ts";

/**
 * Overdue is a **phase**, not a stored status, so it can't be a server-side
 * `status` filter like the others. It filters the page client-side, which is
 * safe only because it never changes which rows the cursor walks — see the
 * note where it's applied.
 */
const TABS = [
  { key: "all", label: "All" },
  { key: "requested", label: "Requested" },
  { key: "dispatched", label: "Dispatched" },
  { key: "delivered", label: "Delivered" },
  { key: "overdue", label: "Overdue" },
  { key: "converted", label: "Converted" },
  { key: "returned", label: "Returned" },
] as const;
type Tab = (typeof TABS)[number]["key"];

const STORED_TABS = ["requested", "dispatched", "delivered", "converted", "returned"] as const;
const isStoredTab = (t: Tab): t is (typeof STORED_TABS)[number] =>
  (STORED_TABS as readonly string[]).includes(t);

const EMPTY_COUNTS = {
  all: 0,
  requested: 0,
  dispatched: 0,
  delivered: 0,
  converted: 0,
  returned: 0,
  overdue: 0,
  outValuePaise: "0",
};

const COL_COUNT = 7;
const PAGE_SIZE = 25;

function emptyMessage(q: string, tab: Tab): string {
  if (q) return "No samples match that search.";
  if (tab === "all") return "No samples yet. They arrive when a buyer requests one, or when you raise one for them.";
  if (tab === "overdue") return "Nothing overdue — every sample that's out is still within its return date.";
  return `No ${SAMPLE_PHASE_LABEL[tab].toLowerCase()} samples.`;
}

/**
 * Store admin — Sampling queue (Commerce, M3).
 *
 * Physical stock sent to a buyer before a bulk order. The list exists to answer
 * two questions: what has been asked for that nobody has actioned, and what is
 * out past its return date.
 *
 * @see packages/domain/src/commerce/store/sample-transitions.ts
 */
export function SamplesListPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);

  const list = trpc.storeSamples.list.useQuery({
    limit: PAGE_SIZE,
    ...(isStoredTab(tab) ? { status: tab } : {}),
    ...(q ? { q } : {}),
    ...(cursor ? { cursor } : {}),
  });
  const counts = trpc.storeSamples.counts.useQuery();
  const c = counts.data ?? EMPTY_COUNTS;

  const all = list.data?.items ?? [];
  // The one filter applied on the page rather than in SQL. It is honest here
  // because `overdue` isn't a column — and the cost is visible: the Overdue tab
  // shows the overdue rows *of this page*, so its footer count can trail the
  // badge. Filtering in SQL would need the same date predicate the counts query
  // already uses; worth doing if a store ever runs more than a page of samples.
  const rows = tab === "overdue" ? all.filter((s) => s.phase === "overdue") : all;

  const resetPaging = () => setCursors([]);

  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Sampling" }]}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Sampling</h1>
          <p className="mt-1.5 text-sm text-muted">
            <span className="font-mono tabular-nums text-ink">{sampleMoney(c.outValuePaise)}</span>{" "}
            of stock out ·{" "}
            <span
              className={
                c.overdue > 0 ? "font-mono tabular-nums text-bad" : "font-mono tabular-nums"
              }
            >
              {c.overdue}
            </span>{" "}
            past return date
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded border border-line bg-paper shadow">
        <TabsRoot
          value={tab}
          onValueChange={(v) => {
            setTab(v as Tab);
            resetPaging();
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line px-4">
            <TabsList aria-label="Filter samples" className="border-b-0 overflow-y-hidden">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} count={c[t.key]}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex flex-wrap items-center gap-2 py-2">
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  resetPaging();
                }}
                placeholder="Search sample # / buyer…"
                aria-label="Search samples"
                className="h-8 w-full min-w-60 sm:w-72"
              />
            </div>
          </div>
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} forceMount className="hidden" aria-hidden />
          ))}
        </TabsRoot>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-4 text-left font-medium">#</th>
                <th className="px-3 py-4 text-left font-medium">Buyer</th>
                <th className="px-3 py-4 text-left font-medium">Requested</th>
                <th className="px-3 py-4 text-right font-medium">Pieces</th>
                <th className="px-3 py-4 text-right font-medium">Value</th>
                <th className="px-3 py-4 text-left font-medium">Return by</th>
                <th className="px-3 py-4 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="px-3 py-3" colSpan={COL_COUNT}>
                        <Skeleton className="h-6 w-full" />
                      </td>
                    </tr>
                  ))
                : rows.map((s) => (
                    <tr key={s.sampleId} className="border-b border-line last:border-0 hover:bg-raised">
                      <td className="px-3 py-3">
                        <Link
                          to="/store/admin/sampling/$sampleId"
                          params={{ sampleId: s.sampleId }}
                          className="font-mono text-sm font-medium text-ink hover:text-sky-700"
                        >
                          {s.docNumber || "Unnumbered"}
                        </Link>
                      </td>
                      <td className="px-3 py-3">
                        <span className="block text-ink">{s.customerName}</span>
                        {/* Who asked matters: a self-serve request is a lead the
                            store didn't chase. */}
                        {s.buyerId ? (
                          <span className="block text-xs text-muted">Requested from storefront</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {shortDate(s.requestedAt)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {s.pieceCount}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {sampleMoney(s.valuePaise)}
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {s.returnBy ?? "—"}
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill tone={SAMPLE_PHASE_TONE[s.phase]} size="sm" dot>
                          {SAMPLE_PHASE_LABEL[s.phase]}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
              {!list.isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-12 text-center text-sm text-muted">
                    {emptyMessage(q, tab)}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          <span>
            Showing <span className="font-mono font-semibold text-ink">{rows.length}</span>{" "}
            {rows.length === 1 ? "sample" : "samples"}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              intent="secondary"
              size="sm"
              disabled={cursors.length === 0}
              onClick={() => setCursors((p) => p.slice(0, -1))}
            >
              ← Prev
            </Button>
            <Button
              intent="secondary"
              size="sm"
              disabled={!list.data?.nextCursor}
              onClick={() => {
                const next = list.data?.nextCursor;
                if (next) setCursors((p) => [...p, next]);
              }}
            >
              Next →
            </Button>
          </div>
        </div>
      </div>
    </StoreAdminLayout>
  );
}
